import {
  Address_Create,
  Address_Read_Nested,
  Address_Update,
} from '@treadinc/horizon-api-spec';

import { countries } from '~constants/enums';
import { Nullable } from '~types/Nullable';

type CountryType = { name: string; code: string } | undefined;

// The shape the company forms keep an address in: a plain object built by
// `getCompanyDefaultValues`, NOT an `AddressItem` instance. The deparse methods below
// accept both, because reading instance-only properties off a form value is exactly
// how CUS-447 dropped the contact names.
export interface AddressFormValue {
  administrativeArea?: string;
  country?: { code?: string; name?: string } | null;
  id?: string;
  lat?: number | null;
  lng?: number | null;
  locality?: string;
  placeId?: string;
  postalCode?: string;
  premise?: string;
  shortName?: string | null;
  streetAddress?: string;
}

// `AddressItem.lat`/`lng` parse to NaN when the stored coordinate is unset, and a form
// value carries null/undefined instead. One guard covers all three.
const coordinateOrUndefined = (coordinate: number | null | undefined) =>
  Number.isFinite(coordinate) ? String(coordinate) : undefined;

// `parse` below substitutes '0.0' for EACH null coordinate independently, so a zero on
// either axis means "not geocoded" rather than the equator or prime meridian — no Tread
// address legitimately sits on either line. Shared so every caller deciding whether an
// address can act as a map centre agrees on the answer.
export const addressIsGeocoded = (
  lat: number | null | undefined,
  lng: number | null | undefined,
) => Number.isFinite(lat) && Number.isFinite(lng) && lat !== 0 && lng !== 0;

export class AddressItem {
  public static parse(proto: Address_Read_Nested, fullAddress?: string) {
    return new AddressItem(
      proto?.thoroughfare ?? '',
      proto.premise ?? '',
      proto?.locality ?? '',
      proto?.administrative_area ?? '',
      proto?.postal_code ?? '',
      proto?.country
        ? countries.values.find((country) => country.code === proto.country)
        : countries.default,
      proto?.lat ?? '0.0',
      proto?.lon ?? '0.0',
      proto?.place_id ?? '',
      proto.id ?? '',
      null,
      fullAddress ?? '',
    );
  }

  public static deparse(proto: Nullable<AddressItem>) {
    if (!proto?.streetAddress) {
      return undefined;
    }

    const result: Address_Read_Nested = {
      thoroughfare: proto?.shortName || proto?.streetAddress || '',
      premise: proto?.premise,
      locality: proto?.locality,
      administrative_area: proto?.administrativeArea,
      postal_code: proto?.postalCode,
      country: proto.country?.code || '',
      lat: coordinateOrUndefined(proto.lat) ?? null,
      lon: coordinateOrUndefined(proto.lng) ?? null,
      place_id: proto.placeId,
      id: proto.id,
    };

    return result;
  }

  public static deparseCreate(proto: Nullable<AddressItem>) {
    if (!proto?.streetAddress) {
      return undefined;
    }

    const result: Address_Create = {
      thoroughfare: proto?.shortName || proto?.streetAddress || '',
      premise: proto?.premise,
      locality: proto?.locality,
      administrative_area: proto?.administrativeArea,
      postal_code: proto?.postalCode,
      country: proto.country?.code || '',
      lat: proto.lat !== null && !isNaN(proto.lat) ? proto.lat.toString() : undefined,
      lon: proto.lng !== null && !isNaN(proto.lng) ? proto.lng.toString() : undefined,
      place_id: proto.placeId,
    };

    return result;
  }

  public static deparseUpdate(proto: Nullable<AddressItem | AddressFormValue>) {
    if (!proto?.streetAddress) {
      return undefined;
    }

    const result: Address_Update = {
      thoroughfare: proto?.shortName || proto?.streetAddress || '',
      premise: proto?.premise,
      locality: proto?.locality,
      administrative_area: proto?.administrativeArea,
      postal_code: proto?.postalCode,
      country: proto.country?.code || '',
      lat: coordinateOrUndefined(proto.lat),
      lon: coordinateOrUndefined(proto.lng),
      place_id: proto.placeId,
    };

    return result;
  }

  public get streetAddress() {
    return this._thoroughfare;
  }

  public get shortName() {
    return this._short_name;
  }

  public get premise() {
    return this._premise;
  }
  public set premise(value: string) {
    this._premise = value;
  }

  public get locality() {
    return this._locality;
  }
  public set locality(value: string) {
    this._locality = value;
  }

  public get administrativeArea() {
    return this._administrative_area;
  }
  public set administrativeArea(value: string) {
    this._administrative_area = value;
  }

  public get postalCode() {
    return this._postal_code;
  }
  public set postalCode(value: string) {
    this._postal_code = value;
  }

  public get country() {
    return this._country;
  }

  public get lat() {
    return parseFloat(this._lat);
  }

  public get lng() {
    return parseFloat(this._lon);
  }

  public get placeId() {
    return this._place_id;
  }

  public get id() {
    return this._id;
  }

  public get thoroughfare() {
    return this._thoroughfare;
  }

  public get fullAddress() {
    return this._full_address;
  }

  public get nameWithAddress() {
    const address = this._full_address || this._thoroughfare;
    const nameIsRedundant =
      !this._short_name || !address || address.startsWith(this._short_name);

    return nameIsRedundant ? address : `${this._short_name} — ${address}`;
  }

  constructor(
    private _thoroughfare: string,
    private _premise: string,
    private _locality: string,
    private _administrative_area: string,
    private _postal_code: string,
    private _country: CountryType,
    private _lat: string,
    private _lon: string,
    private _place_id: string,
    private _id: string,
    private _short_name: string | null,
    private _full_address: string | null,
  ) {}
}

// A saved address in a form is a plain object with no `nameWithAddress` getter.
export const addressAutocompleteLabel = (item: AddressItem | null) =>
  item?.nameWithAddress || item?.streetAddress || '';
